/**
 * 角色管理 API 模块
 * 所有 HTTP 请求通过 api/client.ts 的 request<T>
 * Story 6.3 / 6.4 → Story 11.2 企业角色体系对齐
 *
 * 后端字段映射:
 *   CompanyRole: id → id, name → name, description → description
 */
import { get, post, put, del } from '../client'

// ─── 后端 → 前端 响应映射 ──────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
function mapRole(item: any): RoleInfo {
  return {
    id: item.id ?? item.authorityId,
    code: item.code ?? item.roleCode ?? '',
    name: item.name ?? item.authorityName ?? '',
    description: item.description ?? '',
    corePurpose: item.corePurpose ?? item.purpose ?? '',
    isSystem: false, // 企业角色无「系统角色」概念
    isDefault: item.isDefault === true || Number(item.isDefault) === 1,
    memberCount: item.memberCount ?? item.userCount ?? item.employeeCount ?? 0,
    memberPreview: Array.isArray(item.memberPreview) ? item.memberPreview : [],
    companyId: item.companyId,
    createdAt: item.createdAt ?? '',
    status:
      item.status === 'disabled' ||
      Number(item.status) === 0 ||
      item.enabled === false ||
      item.enable === 2
        ? 'disabled'
        : 'active',
    dataScope: item.dataScope,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ─── API 函数 ──────────────────────────────────────────────

/** 获取当前企业的角色汇总列表，支持服务端分页、角色/用户名称和状态筛选。 */
export async function getRoleList(
  params: RoleListParams = { page: 1, pageSize: 100 },
): Promise<RoleListRes> {
  const res = await get<{ list: unknown[]; total?: number; totalMemberCount?: number }>(
    '/business/role/getRoleList',
    { params: { ...params } },
  )
  return {
    list: (res.list || []).map(mapRole),
    total: res.total,
    totalMemberCount: res.totalMemberCount,
  }
}

/** 使用统一接口新增或完整保存企业角色配置。 */
export async function saveRoleConfig(data: SaveRoleConfigReq): Promise<RoleInfo> {
  const res = await post<unknown>('/business/companyRole/saveCompanyRoleConfig', {
    ...(data.id ? { id: data.id } : {}),
    name: data.name,
    description: data.description,
    status: data.status === 'active' ? 1 : 0,
    permissionIds: data.permissionIds,
    dataScope: data.dataScope,
  })
  return mapRole(res)
}

/** 新增企业角色（biz_company_roles） */
export async function createRole(data: CreateRoleReq): Promise<RoleInfo> {
  const res = await post<RoleInfo>('/business/companyRole/createCompanyRole', {
    companyId: data.companyId,
    name: data.name,
    description: data.description ?? '',
    sortOrder: 0,
  })
  return mapRole(res)
}

/** 删除企业角色（biz_company_roles） */
export function deleteRole(roleId: number) {
  return del<null>('/business/companyRole/deleteCompanyRole', { params: { id: roleId } })
}

/** 更新企业角色名称 */
export function updateRole(data: {
  id: number
  name: string
  description?: string
  status?: RoleStatus
}) {
  return put<null>('/business/companyRole/updateCompanyRole', {
    id: data.id,
    name: data.name,
    description: data.description ?? '',
    ...(data.status === undefined ? {} : { status: data.status === 'active' ? 1 : 0 }),
  })
}

/** 获取当前登录用户的权限码列表（无需 BizPermission，仅 JWT 认证） */
export function getMyPermissions() {
  return get<MyPermissionsRes>('/business/role/getMyPermissions')
}

/** 获取客户端权限树（app_type='client'） */
export function getPermissionTree() {
  return get<{ tree: BackendPermissionModule[] }>('/business/role/getPermissionTree', {
    params: { appType: 'client' },
  })
}

/** 获取企业角色已绑定的客户端权限 ID 列表 */
export function getRolePermissions(roleId: number) {
  return get<{ roleId: number; permissionIds: number[] }>('/business/role/getRolePermissions', {
    params: { roleId },
  })
}

/** 保存企业角色的客户端权限绑定 */
export function saveRolePermissions(data: { roleId: number; permissionIds: number[] }) {
  return put<null>('/business/role/saveRolePermissions', data)
}

/** 获取角色的数据权限配置 */
export function getRoleDataScope(roleId: number) {
  return get<{ dataScope: DataScope; customDeptIds: number[] }>('/business/role/getDataScope', {
    params: { roleId },
  })
}

/** 保存角色的数据权限 */
export function saveRoleDataScope(data: SaveDataScopeReq) {
  return put<null>('/business/role/updateDataScope', data)
}

/** 获取角色已选项目/仓库及同公司可选资源。 */
export function getRoleResources(roleId: number) {
  return get<RoleResourcesRes>('/business/companyRole/getCompanyRoleResources', {
    params: { roleId },
  })
}

/** 全量保存角色的项目与仓库资源范围。 */
export function saveRoleResources(data: SaveRoleResourcesReq) {
  return put<null>('/business/companyRole/updateCompanyRoleResources', data)
}
